import React, { FC, useEffect, useState } from 'react';
import { Stack, Typography, Button as MuiButton, Box } from '@mui/material';
import ModalPortal from '../../common/ModalPortal/ModalPortal';
import Card from '../../common/Card/Card';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import Shape from '../../../@types/Shape';
import * as yup from 'yup';
import Cart from '../../../@types/Cart';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import Address from '../../../@types/Address';
import AddressField from '../../common/Form/FormFields/AddressField/AddressField';
import TextField from '../../common/Form/FormFields/TextField/TextField';
import SelectOptions from '../../common/Form/FormFields/Select/Select';
import SelectOption from '../../../@types/SelectOption';
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import axios from '../../../api/axios';
import flatten from 'flat';
import validateZipCode from '../../../utils/validateZipCode';

interface SaveBuildFormModalProps {
  close: () => void,
  cartBuild: Cart,
}

const SaveBuildFormModal: FC<SaveBuildFormModalProps> = ({ close, cartBuild }) => {
  const [loading, setLoading] = useState<boolean>(false);

  // Handle captcha generation
  useEffect(() => {
    loadCaptchaEnginge(6);
  }, [loadCaptchaEnginge])

  const onSubmit = async (data: SaveBuild) => {
    try {
      setLoading(true);
      console.log(data);
      await axios.post('/lead', data);
      close();
      toast.success('Please check your email address for saved build data');
    } catch (error) {
      console.log(error);
      toast.error('Server Error');
    } finally {
      setLoading(false)
    }
  }

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  }

  // Prevent event bubbling up to parent form (submit event)
  const handleSubmitWithoutPropagation = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    handleSubmit(onSubmit, onError)(e);
  }

  interface SaveBuild {
    firstName: string,
    lastName: string,
    phone: string,
    email: string,
    cartBuild: Cart,
    optIn: boolean,
    address: Address,
    purchaseTimeFrame: string,
    captcha: string,
    leadType: string
  }

  const saveBuildSchema = yup.object<Shape<SaveBuild>>({
    firstName: yup.string().required('Please enter first name'),
    lastName: yup.string().required('Please enter last name'),
    phone: yup.string().optional(),
    email: yup.string()
      .strict()
      .trim('email address cannot include leading or trailing spaces')
      .email('Please enter a valid email address')
      .required(`Please enter an email address`),
    cartBuild: yup.object<Shape<Cart>>().required(),
    purchaseTimeFrame: yup.string().required('Please select a purchase timeframe'),
    address: yup.object<Shape<Address>>({
      street: yup.string().required('Please enter a street address'),
      street2: yup.string(),
      city: yup.string().required('Please enter a city'),
      state: yup.string().when('countryCode', (value) => {
        if (value !== 'US' || value !== 'CA') {
          return yup.string().optional();
        } else {
          return yup.string().required('Please enter a state / province');
        }
      }),
      stateCode: yup.string().when('countryCode', (value) => {
        if (value !== 'US' || value !== 'CA') {
          return yup.string().optional();
        } else {
          return yup.string().required('Please enter a state / province');
        }
      }),
      zip: yup.string()
        .required('Please enter a zip or postal code')
        .when('countryCode', (value) => {
          return yup.string().required(`Invalid zip or postal code for country [${value}]`)
            .test('is-valid-zipcode', `Invalid zip or postal code for country [${value}]`, function () {
              const { zip, countryCode } = this.parent;
              return validateZipCode(countryCode, zip)
            })
        }),
      country: yup.string().required('Please enter a country'),
      countryCode: yup.string().required('Please enter a country code'),
      lat: yup.number().required('Please enter address lat'),
      lng: yup.number().required('Please enter address lng')
    }),
    optIn: yup.boolean().required(),
    captcha: yup.string().required('Please enter captcha').test('validated',
      `Please enter in a valid captcha`,
      (value) => validateCaptcha(value, false)
    ),
    leadType: yup.string().required('Please enter lead type')
  });

  const defaultValues: any = {
    cartBuild: cartBuild,
    leadType: 'Build Your Own',
    optIn: true
  };

  const methods = useForm<SaveBuild>({
    defaultValues: defaultValues,
    resolver: yupResolver(saveBuildSchema)
  });

  const purchaseTimeFrameOptions: SelectOption[] = [
    {
      label: 'Within 3 Months',
      value: 'Within 3 Months',
    },
    {
      label: '3-6 Months',
      value: '3-6 Months',
    },
    {
      label: '6-12 Months',
      value: '6-12 Months',
    },
    {
      label: '12+ Months',
      value: '12+ Months',
    }
  ]

  const { handleSubmit, trigger, watch, formState: { isSubmitted } } = methods;

  const countryCode = watch('address.countryCode');

  useEffect(() => {

    if (isSubmitted) {

      trigger('address.zip');

      trigger('phone');

    }

  }, [countryCode, isSubmitted, trigger]);

  return (
    <ModalPortal>
      <Stack
        width="100vw"
        minHeight="100vh"
        alignItems="center"
        paddingTop="2%">
        <Card
          sx={(theme) => ({
            overflowY: 'auto',
            borderRadius: '4px',
            width: "clamp(300px, calc(100% - 50px), 900px)",
            maxHeight: "clamp(200px, calc(100vh - 80px), 1300px)",
            [theme.breakpoints.down('tabletS')]: {
              maxHeight: "clamp(200px, calc(100vh - 50px), 1300px)",
            }
          })}>
          <Stack
            padding="2%"
            direction="column" spacing={2}>
            <Stack
              direction="row"
              alignItems="flex-end"
              justifyContent="flex-end">
              <MuiButton
                onClick={close}
                sx={{
                  '& .MuiTouchRipple-child': {
                    backgroundColor: 'grey'
                  }
                }}>
                <CloseIcon sx={{ color: 'grey' }} />
              </MuiButton>
            </Stack>
            <Stack
              width="100%"
              direction="row"
              alignItems="center"
              justifyContent="center">
              <Typography
                color="#e20b23"
                lineHeight={1.2}
                textTransform="uppercase"
                fontSize={24}
                letterSpacing={3}
                variant="primaryFont">
                Save your build
              </Typography>
            </Stack>
            <Stack direction="column">
              <FormProvider {...methods}>
                <form onSubmit={handleSubmitWithoutPropagation}>
                  <Stack direction="column" spacing={2}>
                    <Stack
                      width="100%"
                      direction="row"
                      flexWrap='wrap'
                      useFlexGap
                      spacing={2}
                      sx={{
                        '& .MuiStack-root': { width: 'fit-content', flexGrow: 1 }
                      }}>
                      <TextField name="firstName" label="First Name" />
                      <TextField name="lastName" label="Last Name" />
                    </Stack>
                    <Stack
                      width="100%"
                      direction="row"
                      flexWrap='wrap'
                      useFlexGap
                      spacing={2}
                      sx={{
                        '& .MuiStack-root': { width: 'fit-content', flexGrow: 1 }
                      }}>
                      <TextField name="phone" label="Phone Number" />
                      <TextField name="email" label="Email" />
                    </Stack>
                    <AddressField name="address" label="" showGoogleMapValidation={false} />

                    <SelectOptions name="purchaseTimeFrame" label="Purchase Timeframe" options={purchaseTimeFrameOptions} />

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        paddingTop: '24px',
                        paddingBottom: '24px',
                        '& .MuiStack-root': { width: 'fit-content', flexGrow: 1 }
                      }}>

                      <Box maxWidth="180px">

                        <LoadCanvasTemplate width="fit-content" reloadText="Reload My Captcha" reloadColor="red" />

                      </Box>

                      <TextField name="captcha" label="Please Enter Captcha" />

                    </Stack>

                    {/* <CheckBox
                      hidden={true}
                      name="optIn"
                      label="I would like to receive additional communications from MadJax"
                      isTrueFalse={true}
                      defaultValue={1} /> */}
                    {/* <Typography
                      variant="secondaryFont"
                      fontSize="12px"
                      fontStyle="italic">
                      By opting into this form, you agree to receive communications, updates, and promotional material from us via email or other communication channels, and acknowledge that you can unsubscribe or opt-out at any time.
                      We will handle your personal information in accordance with our privacy policy.
                    </Typography> */}
                    <LoadingButton
                      loading={loading}
                      type="submit">
                      Submit
                    </LoadingButton>
                  </Stack>
                </form>
              </FormProvider>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </ModalPortal>
  );
}

export default SaveBuildFormModal;
